import * as THREE from 'three';
import type { Player } from '@entities/Player';
import type { Enemy } from '@entities/Enemy';
import { GAME_CONFIG } from 'shared';

export interface DamageNumber {
  value: number;
  position: THREE.Vector3;
  element: HTMLDivElement;
  startTime: number;
  isCrit: boolean;
}

export class CombatSystem {
  private player: Player;
  private lastAttackTime: number = 0;
  private attackCooldown: number = GAME_CONFIG.ATTACK_COOLDOWN;
  private attackRange: number = GAME_CONFIG.ATTACK_RANGE_MELEE;
  private baseDamage: number = 15;

  // Damage numbers
  private damageNumbers: DamageNumber[] = [];
  private damageContainer: HTMLDivElement;

  // Camera reference for projecting 3D to 2D
  private camera: THREE.Camera;

  constructor(player: Player, camera: THREE.Camera) {
    this.player = player;
    this.camera = camera;

    // Create container for damage numbers
    this.damageContainer = document.createElement('div');
    this.damageContainer.id = 'damage-numbers';
    this.damageContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;
      z-index: 50;
    `;
    document.getElementById('game-container')?.appendChild(this.damageContainer);
  }

  public canAttack(): boolean {
    return Date.now() - this.lastAttackTime >= this.attackCooldown;
  }

  public attack(target: Enemy): { hit: boolean; damage: number; killed: boolean } {
    if (!this.canAttack()) {
      return { hit: false, damage: 0, killed: false };
    }

    const playerPos = this.player.getPosition();
    const enemyPos = target.getPosition();
    const distance = playerPos.distanceTo(enemyPos);

    if (distance > this.attackRange) {
      return { hit: false, damage: 0, killed: false };
    }

    this.lastAttackTime = Date.now();

    // Calculate damage with variance
    const isCrit = Math.random() < 0.15; // 15% crit chance
    const variance = 0.8 + Math.random() * 0.4; // 80%-120% damage
    let damage = Math.floor(this.baseDamage * variance);

    if (isCrit) {
      damage = Math.floor(damage * 2);
    }

    // Apply damage
    const killed = target.takeDamage(damage);

    // Show damage number
    this.showDamageNumber(damage, enemyPos, isCrit);

    return { hit: true, damage, killed };
  }

  private showDamageNumber(
    value: number,
    position: THREE.Vector3,
    isCrit: boolean
  ): void {
    const element = document.createElement('div');
    element.textContent = value.toString();
    element.style.cssText = `
      position: absolute;
      font-family: 'Segoe UI', Arial, sans-serif;
      font-weight: bold;
      font-size: ${isCrit ? '28px' : '20px'};
      color: ${isCrit ? '#ff0' : '#fff'};
      text-shadow: 2px 2px 4px rgba(0,0,0,0.8), 0 0 10px ${isCrit ? 'rgba(255,255,0,0.5)' : 'rgba(255,0,0,0.5)'};
      pointer-events: none;
      transform: translate(-50%, -50%);
      z-index: 51;
    `;

    this.damageContainer.appendChild(element);

    const damageNumber: DamageNumber = {
      value,
      position: position.clone().add(new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        1.5,
        (Math.random() - 0.5) * 0.5
      )),
      element,
      startTime: Date.now(),
      isCrit,
    };

    this.damageNumbers.push(damageNumber);
  }

  public update(): void {
    const now = Date.now();

    // Update damage numbers
    for (let i = this.damageNumbers.length - 1; i >= 0; i--) {
      const dn = this.damageNumbers[i];
      const age = now - dn.startTime;

      if (age > 1000) {
        // Remove old damage numbers
        dn.element.remove();
        this.damageNumbers.splice(i, 1);
        continue;
      }

      // Animate: float up and fade out
      dn.position.y += 0.02;

      // Project 3D position to 2D screen
      const screenPos = this.worldToScreen(dn.position);

      if (screenPos) {
        dn.element.style.left = `${screenPos.x}px`;
        dn.element.style.top = `${screenPos.y}px`;
        dn.element.style.opacity = (1 - age / 1000).toString();
      } else {
        dn.element.style.opacity = '0';
      }
    }
  }

  private worldToScreen(position: THREE.Vector3): { x: number; y: number } | null {
    const vector = position.clone();
    vector.project(this.camera);

    // Check if in front of camera
    if (vector.z > 1) {
      return null;
    }

    const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
    const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

    return { x, y };
  }

  public getAttackCooldownProgress(): number {
    const elapsed = Date.now() - this.lastAttackTime;
    return Math.min(1, elapsed / this.attackCooldown);
  }

  public destroy(): void {
    this.damageContainer.remove();
  }
}
